import { MethodsType, GetSchema, ApiInterfaces } from '@/define';
import { ParametersDef } from '@/define';
import Store, { ErrorType } from '@/store';
import { initArray, pascalCase, camelcase } from '@/helpers/utils';
const clone = require('clone');

export const pascalCaseReplaceString = (str: string, pascalCase = true) => {
  // 移除路径中的特殊字符，包括冒号（用于路由命名空间如 session:encrypt）
  return camelcase(str.replace(/[\/|\{|\}|\?|\$|:]/g, ''), { pascalCase });
};

export const renderMethodArgs = (args: string[]): string => {
  return initArray(args)
    .filter((item) => item)
    .join(',');
};
/**
 * 标准化并格式化 URL
 * 1. 移除路径参数中的正则表达式 {userid:\d+} -> {userid}
 * 2. 将路径参数转换为模板字符串格式 {userid} -> ${userid}
 */
export const normalizeUrl = (parameters: ParametersDef[] = [], url: string): string => {
  // 从 parameters 中提取所有可能的路径参数名
  const pathParamNames = new Set<string>();
  
  // 优先从 in: "path" 的参数中提取
  parameters.forEach(param => {
    if (param.in === 'path' && param.name) {
      pathParamNames.add(param.name);
    }
  });

  // 容错处理：如果 parameters 中有参数名匹配 URL 中的 {xxx} 格式，也认为是路径参数
  const urlParamPattern = /\{([^:}]+)(?::[^}]*)?\}/g;
  let match;
  const urlCopy = url; // 保存原始URL用于匹配
  while ((match = urlParamPattern.exec(urlCopy)) !== null) {
    const paramName = match[1];
    // 检查 parameters 中是否有这个参数名（不限 in 类型）
    const foundParam = parameters.find(p => p.name === paramName);
    if (foundParam) {
      pathParamNames.add(paramName);
    }
  }

  // 替换 URL 中的复杂路径参数为简单格式
  // {userid:\d+} -> {userid}
  // {type:[\w\+=\-]+} -> {type}
  let normalizedUrl = url;
  if (pathParamNames.size > 0) {
    normalizedUrl = url.replace(/\{([^:}]+)(?::[^}]*)?\}/g, (match, paramName) => {
      // 如果这个参数名在我们的列表中，使用简化格式
      if (pathParamNames.has(paramName)) {
        return `{${paramName}}`;
      }
      // 否则保持原样（虽然这种情况应该很少）
      return match;
    });
  } else {
    // 如果没有找到参数定义，也尝试简化（容错）
    normalizedUrl = url.replace(/\{([^:}]+)(?::[^}]*)?\}/g, '{$1}');
  }

  return normalizedUrl;
};

export const formatUrl = (parameters: ParametersDef[] = [], url: string) => {
  // 先标准化 URL
  const normalized = normalizeUrl(parameters, url);
  
  // 然后将路径参数转换为模板字符串格式
  const pathParams = initArray(parameters).filter((paramsItem) => paramsItem.in === 'path');
  return pathParams.reduce((pre, { name }) => {
    return pre.replace(`{${name.trim()}}`, `$\{${name.trim()}}`);
  }, normalized);
};
export const createMethodsName = (url: string, method: string, parameters: any[] = []) => {
  // 使用标准化的 URL 生成方法名
  const normalized = normalizeUrl(parameters, url);
  return pascalCaseReplaceString(`${normalized}${pascalCase(method)}`);
};
export const formatParamtersItemType = (type: ParametersDef['type']) => {
  if (type === 'integer') {
    return 'number';
  }
  return 'string';
};
// 获取parameters中path的参数
export const renderRenderParametersPaths = (parmeters: ParametersDef[], isTypeScript = true) => {
  return initArray(parmeters)
    .reduce((pre: string[], next) => {
      if (next.in === 'path') {
        if (isTypeScript) {
          pre.push(`${next.name}: ${formatParamtersItemType(next.type)}`);
          return pre;
        }
        pre.push(`${next.name}`);
        return pre;
      }
      return pre;
    }, [])
    .join(',');
};

export const getParameterPath = (parmeters: ParametersDef[]): ParametersDef[] => {
  return initArray(parmeters).reduce((pre: ParametersDef[], next) => {
    if (next.in === 'path') {
      pre.push(next);
      return pre;
    }
    return pre;
  }, []);
};
export const getParameterData = (parmeters: ParametersDef[]) => {
  return initArray(parmeters).reduce((pre: ParametersDef[], next) => {
    if (next.in === 'body') {
      pre.push(next);
      return pre;
    }
    return pre;
  }, []);
};
export const getParameterParams = (parmeters: ParametersDef[]) => {
  return initArray(parmeters).reduce((pre: ParametersDef[], next) => {
    if (next.in === 'query') {
      pre.push(next);
      return pre;
    }
    return pre;
  }, []);
};

// 渲染模板头部
export const getTemplatePrefix = () => {
  if (Store.isTypeScript) {
    return `
          import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
          \n
      `;
  }
  return `import axios from 'axios'\n`;
};

// const deepCopy = (json: any) => {
//   return clone(json);
// };
// const formatTsResponse = (name: string) => {
//   return `${name}Response`;
// };
// const createTypeDefineObj = (title: string, properties: any, type: string, required?: any) => {
//   return {
//     key: title,
//     propertiesKey: {
//       $ref: `#/definitions/${title}`,
//     },
//     definitionsKey: {
//       title: title,
//       type: type,
//       additionalProperties: false,
//       properties: properties,
//       required: required,
//     },
//   };
// };

const wapperDefineName = (methName: string, mode: 'params' | 'data' | 'response') => {
  if (mode === 'params') {
    return `${methName}RequsetParams`;
  }
  if (mode === 'data') {
    return `${methName}RequsetData`;
  }
  return `${methName}Response`;
};
export class Helper {
  public uri: string;
  public method: MethodsType;
  public sourceSchema: GetSchema;
  public summary: string;
  public description: string;
  public getApiInteraces: ApiInterfaces[] = [];
  renderOptionsStr = {
    params: '',
    data: '',
  };
  
  constructor(uri: string, method: MethodsType, params: GetSchema) {
    this.uri = uri;
    this.method = method;
    this.sourceSchema = params;
    this.description = params.description;
    this.summary = params.summary;
  }
  getUrl() {
    return formatUrl(this.sourceSchema.parameters, this.uri);
  }
  getMethodsName() {
    return createMethodsName(this.uri, this.method, this.sourceSchema.parameters);
  }
  getJsDocTypes() {
    return `{import("./${Store.filename}.types.ts").${this.getMethodsName()}}`;
  }
  getMethodPrePath() {
    const getPaths = renderRenderParametersPaths(this.sourceSchema.parameters, Store.isTypeScript);
    this.__vaildateGetMethodPrePath(getParameterPath(this.sourceSchema.parameters));
    return getPaths ? `${getPaths}` : '';
  }
  getMethodPreParams() {
    const getParams = getParameterParams(this.sourceSchema.parameters);
    if (getParams.length === 0) {
      return '';
    }
    this.renderOptionsStr.params = 'params';
    if (Store.isTypeScript) {
      return `params: P`;
    }
    return 'params';
  }
  getJsDocMethodPreParams() {
    const getParams = getParameterParams(this.sourceSchema.parameters);
    if (getParams.length === 0) {
      return '';
    }
    this.renderOptionsStr.params = 'params';
    return `params: P`;
  }
  getMethodOption() {
    if (Store.isTypeScript) {
      return `options: AxiosRequestConfig = {}`;
    }
    return `options = {}`;
  }
  getJsDocMethodOption() {
    return `options: AxiosRequestConfig`;
  }
  // 获取params的ts定义名字
  getMethodPreDefineParams() {
    if (!this.getMethodPreParams()) {
      return;
    }
    return wapperDefineName(this.getMethodsName(), 'params');
  }
  // 校验path参数是否有有问题
  __vaildateGetMethodPrePath(getParams: ParametersDef[]) {
    const matchResult = this.uri.match(/\{\w+\}/g);
    const getUrlParams = (matchResult ? matchResult : []) as string[];
    const getUrlParamsTotal = getUrlParams.length;
    const parameters = getParams.length;
    if (getUrlParamsTotal === 0 && parameters === 0) {
      return;
    }
    // 检查定义的path参数和url上的参数数量是否一致
    if (getUrlParamsTotal !== parameters) {
      Store.pushError({
        url: this.uri,
        desc: this.description,
        method: this.method,
        errorType: ErrorType.PathParams,
      });
    }
    // 检测路径参数类型是否匹配
    const errMsg: string[] = [];
    getUrlParams.forEach((key) => {
      const target = getParams.find((item) => String(key).indexOf(item.name) > -1);
      if (target && target.in !== 'path') {
        errMsg.push(`${key}的in类型为${target.in}, 应该改为path`);
      }
    });
    if (errMsg.length > 0) {
      Store.pushError({
        url: this.uri,
        desc: this.description,
        method: this.method,
        errMsg: errMsg.join(','),
        errorType: ErrorType.PathError,
      });
    }
  }
  debugPrint(desc: string, errMsg = '') {
    if (this.description === desc) {
      Store.pushError({
        errorType: ErrorType.Debug,
        url: this.uri,
        method: this.method,
        desc: this.description,
        errMsg: errMsg,
      });
    }
  }
  // 查找方法中所有params参数
  getMethodParams() {}
  // 查找方法中所有data参数
  getMethodData() {
    return getParameterData(this.sourceSchema.parameters);
  }
  renderGetMethodData() {
    if (this.getMethodData().length === 0) {
      return '';
    }
    this.renderOptionsStr.data = 'data';
    if (Store.isTypeScript) {
      return `data: D`;
    }
    return `data`;
  }
  renderJsDocGetMethodData() {
    if (this.getMethodData().length === 0) {
      return '';
    }
    this.renderOptionsStr.data = 'data';
    return `data: D`;
  }
  // 获取data的ts定义名字
  renderGetMethodDefineData() {
    if (this.method === MethodsType.Get) {
      return;
    }
    if (!this.renderGetMethodData()) {
      return;
    }
    return wapperDefineName(this.getMethodsName(), 'data');
  }
  // 获取接口返回的ts定义名字
  getMethodsDefineResponse() {
    return wapperDefineName(this.getMethodsName(), 'response');
  }
  // 获取返回对象的ts定义名字
  renderTsDefineResFeature() {
    if (!this.sourceSchema.responses['200']) {
      return [];
    }
    return [];
  }

  renderTsDefineReqFeature() {
    const { parameters = [] } = this.sourceSchema;
    const reqParams = initArray(parameters).filter((params) => params.in === 'body');
    if (reqParams.length === 0) {
      return [];
    }
    return [];
  }

  // 处理query data的函数
  generatorDefineParams() {
    const querys = initArray(this.sourceSchema.parameters).filter((parameterItem) => parameterItem.in === 'query');
    const properties = querys.reduce((pre: any, next: any) => {
      const { name, type, description }: { name: string; type: string; description: string } = next;
      pre[name] = {
        type,
        description,
      };
      return pre;
    }, {});
    const required = initArray(querys)
      .filter((item) => item.required)
      .map((item) => item.name);
    const key = this.getMethodPreDefineParams();
    if (!key) {
      return {
        key,
        propertiesKey: {},
        definitionsKey: {},
      };
    }
    return {
      key,
      propertiesKey: {
        $ref: `#/definitions/${key}`,
      },
      definitionsKey: {
        title: key,
        type: 'object',
        additionalProperties: false,
        properties,
        description: this.description,
        required: required,
      },
    };
  }
  // 处理response的函数
  formatProperties(title: string, itemSchema: any) {
    try {
      const deepItemSchema = clone(itemSchema);
      // if (!deepItemSchema.properties && deepItemSchema['$ref']) {
      //   const innerTitle = `${title}${pascalCase('items')}`;
      //   return {
      //     [title]:`#/definitions/${title}`
      //   }
      // }
      const properties = Object.keys(deepItemSchema.properties).reduce((pre: any, next) => {
        const data = deepItemSchema.properties[next];
        const innerTitle = `${title}${pascalCase(next)}`;
        if (['object'].includes(data.type)) {
          pre[next] = {};
          pre[next]['$ref'] = `#/definitions/${innerTitle}`;
          return pre;
        }
        if (['array'].includes(data.type)) {
          pre[next] = {};
          pre[next]['items'] = {};
          pre[next]['items']['$ref'] = `#/definitions/${innerTitle}`;
          return pre;
        }
        pre[next] = {};
        pre[next] = clone(data);
        return pre;
      }, {});
      return properties;
    } catch (err) {
      return {};
    }
  }
  setTypeObjToInstances(preName: string, key: string, itemSchema: any) {
    try {
      if (!key) {
        // 没有key就是root节点
        // 先把这个接口的response根节点写入
        if (itemSchema.type === 'object') {
          const rootTitle = `${preName}`;
          const rootProperties = this.formatProperties(rootTitle, itemSchema);
          const apiInstance = {
            key: rootTitle,
            propertiesKey: {
              $ref: `#/definitions/${rootTitle}`,
            },
            definitionsKey: {
              title: rootTitle,
              type: itemSchema.type,
              properties: rootProperties,
              additionalProperties: false,
              required: initArray(itemSchema.required),
            },
          };
          this.getApiInteraces.push(apiInstance);
          return;
        }
        if (itemSchema.type === 'array') {
          const rootTitle = `${preName}`;
          const rootProperties = this.formatProperties(rootTitle, itemSchema.items);
          const apiInstance = {
            key: rootTitle,
            propertiesKey: {
              items: {
                $ref: `#/definitions/${rootTitle}`,
              },
            },
            definitionsKey: {
              title: rootTitle,
              type: itemSchema.type,
              properties: rootProperties,
              additionalProperties: false,
              required: initArray(itemSchema.required),
            },
          };
          this.getApiInteraces.push(apiInstance);
          return;
        }
      }
      // 剩下就是有key的
      const rootTitle = `${preName}${pascalCase(key)}`;
      if (key === 'children') {
      }
      if (itemSchema.type === 'array') {
        const apiInstance = {
          key: rootTitle,
          propertiesKey: {
            items: {
              $ref: `#/definitions/${rootTitle}`,
            },
          },
          definitionsKey: {
            title: rootTitle,
            type: itemSchema.items.type,
            properties: this.formatProperties(rootTitle, itemSchema.items), // TODO,
            additionalProperties: false,
            required: initArray(itemSchema.required),
          },
        };
        this.getApiInteraces.push(apiInstance);
        return;
      }
      if (itemSchema.type === 'object') {
        const apiInstance = {
          key: rootTitle,
          propertiesKey: {
            $ref: `#/definitions/${rootTitle}`,
          },
          definitionsKey: {
            title: rootTitle,
            type: itemSchema.type,
            properties: this.formatProperties(rootTitle, itemSchema), // TODO,
            additionalProperties: false,
            required: initArray(itemSchema.required),
          },
        };
        this.getApiInteraces.push(apiInstance);
        return;
      }
      (() => {
        const apiInstance = {
          key: rootTitle,
          propertiesKey: {
            $ref: `#/definitions/${rootTitle}`,
          },
          definitionsKey: {
            title: rootTitle,
            type: itemSchema.type,
            additionalProperties: false,
            required: initArray(itemSchema.required),
          },
        };
        this.getApiInteraces.push(apiInstance);
      })();
    } catch (err) {
      const rootTitle = `${preName}${pascalCase(key)}`;
      if (itemSchema.type === 'array') {
        const apiInstance = {
          key: rootTitle,
          propertiesKey: {
            items: {
              $ref: `#/definitions/${rootTitle}`,
            },
          },
          definitionsKey: {
            title: rootTitle,
            additionalProperties: true,
          },
        };
        this.getApiInteraces.push(apiInstance);
      }
      Store.pushError({
        url: this.uri,
        method: this.method,
        errorType: ErrorType.SchemaError,
        desc: this.description,
        errMsg: 'setTypeObjToInstances ' + rootTitle + ' ' + (err as Error).toString(),
      });
    }
  }
  setTypeNotProperties(preName: string, key: string, itemSchema: any) {
    const rootTitle = `${preName}${pascalCase(key)}`;
    const apiInstance = {
      key: rootTitle,
      propertiesKey: {},
      definitionsKey: {
        title: rootTitle,
        type: itemSchema.type,
        additionalProperties: true,
        required: [],
      },
    };
    this.getApiInteraces.push(apiInstance);
  }
  setTypeNotProperToArrRef(preName: string, key: string, itemSchema: any) {
    const rootTitle = `${preName}${pascalCase(key)}`;
    const apiInstance = {
      key: rootTitle,
      propertiesKey: {
        ...itemSchema.items,
      },
      definitionsKey: {
        title: rootTitle,
        type: itemSchema.type,
        additionalProperties: true,
        required: [],
      },
    };
    this.getApiInteraces.push(apiInstance);
  }
  generatorDeepDefine(preName: string, key: string, itemSchema: any) {
    if (itemSchema.type === 'object') {
      // 有key说明不是跟response
      const rootTitle = `${preName}${pascalCase(key)}`;
      if (key && !itemSchema.properties) {
        this.setTypeNotProperties(preName, key, itemSchema);
        return;
      }

      if (key && itemSchema.properties) {
        this.setTypeObjToInstances(preName, key, itemSchema);
        // TODO
      }

      // 深度递归找出里面需要另外设置ref的数据
      Object.keys(itemSchema.properties).forEach((itemKey) => {
        const data = itemSchema.properties[itemKey];
        if (['object'].includes(data.type)) {
          this.generatorDeepDefine(rootTitle, itemKey, clone(data));
        } else if (['array'].includes(data.type)) {
          this.generatorDeepDefine(rootTitle, itemKey, clone(data));
        }
      });
    }
    if (itemSchema.type === 'array') {
      this.setTypeObjToInstances(preName, key, itemSchema);
      if (!itemSchema.items) {
        return;
      }
      if (!key && !itemSchema.items.properties) {
        this.setTypeNotProperToArrRef(preName, key, itemSchema);
        return;
        // 根节点数组的情况
      }
      if (!itemSchema.items.properties) {
        this.setTypeNotProperties(preName, key, itemSchema);
        return;
      }
      const rootTitle = `${preName}${pascalCase(key)}`;
      if (itemSchema.items.type === 'object') {
        Object.keys(clone(itemSchema.items.properties)).forEach((itemKey) => {
          const data = itemSchema.items.properties[itemKey];
          if (['object', 'array'].includes(data.type)) {
            this.generatorDeepDefine(rootTitle, itemKey, clone(data));
          }
        });
      } else if (itemSchema.items.type === 'array') {
        const data = itemSchema.items;
        this.generatorDeepDefine(rootTitle, 'item', clone(data));
      } else {
      }
    }
  }
  // 从 example 字符串中解析出数据结构
  parseExampleToSchema(example: string): any {
    try {
      const parsed = JSON.parse(example);
      return this.inferSchemaFromValue(parsed);
    } catch (err) {
      // 如果解析失败，返回一个空的 object schema
      return {
        type: 'object',
        properties: {},
        additionalProperties: true,
      };
    }
  }

  // 从实际值推断 schema 结构（所有字段都设为 any 类型，非必填）
  inferSchemaFromValue(value: any): any {
    if (value === null || value === undefined) {
      return { type: 'any' };
    }

    if (Array.isArray(value)) {
      const itemSchema = value.length > 0 
        ? this.inferSchemaFromValue(value[0])
        : { type: 'any' };
      return {
        type: 'array',
        items: itemSchema,
      };
    }

    if (typeof value === 'object') {
      const properties: any = {};
      Object.keys(value).forEach(key => {
        // 所有字段都设置为 any 类型
        properties[key] = {
          type: 'any',
          description: '',
        };
      });
      return {
        type: 'object',
        properties,
        additionalProperties: true,
        required: [], // 所有字段都是非必填
      };
    }

    // 基础类型也返回 any
    return { type: 'any' };
  }

  generatorDefineData() {
    return {
      key: '',
      propertiesKey: {},
      definitionsKey: {},
    };
  }
  generatorDefineResponse() {
    const key = this.getMethodsDefineResponse();
    const response = this.sourceSchema.responses['200'];
    if (!response) {
      // 没有200响应，记录错误并生成一个 any 类型的默认定义
      Store.pushError({
        url: this.uri,
        method: this.method,
        errorType: ErrorType.SchemaError,
        desc: this.description,
        errMsg: '缺少 response 200 定义',
      });
      
      const defaultAnyInfo = {
        key,
        propertiesKey: {
          $ref: `#/definitions/${key}`,
        },
        definitionsKey: {
          title: key,
          type: 'object',
          additionalProperties: true,
          description: this.description,
        },
      };
      this.getApiInteraces.push(defaultAnyInfo);
      return this.getApiInteraces;
    }
    const resSchema = response.schema;
    if (!resSchema) {
      const emptyInfof = {
        key,
        propertiesKey: {
          $ref: `#/definitions/${key}`,
        },
        definitionsKey: {
          title: key,
          type: 'object',
          additionalProperties: true,
          description: this.description,
        },
      };
      this.getApiInteraces.push(emptyInfof);
      return this.getApiInteraces;
    }

    // 检查是否只有 example 而没有 properties（边界情况）
    const hasProperties = resSchema.properties || (resSchema.items && resSchema.items.properties);
    const hasExample = resSchema.example;
    
    if (!hasProperties && hasExample && typeof hasExample === 'string') {
      // 只有 example，没有结构化的 properties
      // 尝试从 example 中解析出结构
      const parsedSchema = this.parseExampleToSchema(hasExample);
      
      // 生成一个简单的定义，所有字段都是 any 类型
      const exampleInfo = {
        key,
        propertiesKey: {
          $ref: `#/definitions/${key}`,
        },
        definitionsKey: {
          title: key,
          type: parsedSchema.type || 'object',
          properties: parsedSchema.properties || {},
          additionalProperties: true,
          required: [],
          description: this.description,
        },
      };
      this.getApiInteraces.push(exampleInfo);
      return this.getApiInteraces;
    }

    // 先把根节点的response设置好
    this.setTypeObjToInstances(key, '', resSchema);
    // 编辑response对象
    if (resSchema.type === 'array') {
      this.generatorDeepDefine(key, '', resSchema.items);
    } else {
      this.generatorDeepDefine(key, '', resSchema);
    }
    return this.getApiInteraces;
  }
}
